import React from 'react';
import { Text, Link, mergeClasses } from '@fluentui/react-components';
import {
  DocumentRegular,
  GlobeRegular,
  PersonRegular,
  BuildingRegular,
  KeyRegular,
  Wifi1Regular,
  CalendarLtrRegular,
  ShieldRegular,
  type FluentIcon,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useQuickLinksPanelStyles } from './QuickLinksPanel.styles';
import { iconSize } from '@/theme';
import type { QuickLink } from '@store/models/booking';

const ICON_MAP: Record<string, FluentIcon> = {
  DocumentRegular,
  GlobeRegular,
  PersonRegular,
  BuildingRegular,
  KeyRegular,
  Wifi1Regular,
  CalendarLtrRegular,
  ShieldRegular,
};

const FALLBACK_ICON: FluentIcon = DocumentRegular;

export interface QuickLinksPanelProps {
  links: QuickLink[];
  className?: string;
  /** Optional click handler invoked with the clicked link (e.g. for telemetry). */
  onLinkClick?: (link: QuickLink) => void;
}

export const QuickLinksPanel: React.FC<QuickLinksPanelProps> = ({
  links,
  className,
  onLinkClick,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useQuickLinksPanelStyles();

  if (!links || links.length === 0) return null;

  return (
    <aside
      className={mergeClasses(styles.root, className)}
      aria-label={t('bookingForm.quickLinks.title')}
    >
      <Text as="h3" className={styles.title}>
        {t('bookingForm.quickLinks.title')}
      </Text>
      <ul className={styles.list}>
        {links.map((link) => {
          const Icon = (link.icon && ICON_MAP[link.icon]) || FALLBACK_ICON;
          return (
            <li key={link.url} className={styles.linkItem}>
              <span className={styles.linkIcon} aria-hidden="true">
                <Icon style={{ fontSize: iconSize(16) }} />
              </span>
              <Link
                href={link.url}
                className={styles.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onLinkClick ? () => onLinkClick(link) : undefined}
              >
                {t(link.labelKey)}
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

QuickLinksPanel.displayName = 'QuickLinksPanel';
